
import React, { useState, useEffect, useRef } from 'react';
import { DisplayedImage, CustomFolder } from '../types';
import LoadingSpinner from './LoadingSpinner';
import CloseIcon from './CloseIcon';
import { USER_CREATED_WORK_SPANISH_NAME, USER_EXPORTED_ART_SPANISH_NAME, GENERATED_IMAGES_FASE_PATH } from '../constants';

interface SaveDestinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: DisplayedImage | null;
  onSaveToDrafts: () => void;
  onSaveToFinalArtwork: () => void;
  onSaveToCustomFolder: (customFolder: CustomFolder) => void;
  customFolders: CustomFolder[];
  onAddFolder: (folderName: string) => Promise<boolean>; 
  isSavingToDrafts: boolean;
  isSavingToFinalArtwork: boolean;
  isSavingToCustomFolderId: string | null; 
  onDebugHover?: (name: string | null) => void; // New prop
}

const SaveDestinationModal: React.FC<SaveDestinationModalProps> = ({
  isOpen,
  onClose,
  image,
  onSaveToDrafts,
  onSaveToFinalArtwork,
  onSaveToCustomFolder,
  customFolders,
  onAddFolder,
  isSavingToDrafts,
  isSavingToFinalArtwork,
  isSavingToCustomFolderId,
  onDebugHover,
}) => {
  const [showAddFolderInput, setShowAddFolderInput] = useState(false);
  const [newCustomFolderName, setNewCustomFolderName] = useState('');
  const [addFolderError, setAddFolderError] = useState<string | null>(null);

  const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);
  const [animateCloseClick, setAnimateCloseClick] = useState(false);
  // Fix: Namespace 'global.NodeJS' has no exported member 'Timeout'. Using ReturnType<typeof setInterval> for cross-environment compatibility.
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const prevSavingStates = useRef({
    drafts: isSavingToDrafts,
    final: isSavingToFinalArtwork,
    custom: isSavingToCustomFolderId,
  });

  const clearCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setAutoCloseCountdown(null);
    setAnimateCloseClick(false);
  };

  useEffect(() => {
    const justCompletedDrafts = prevSavingStates.current.drafts && !isSavingToDrafts;
    const justCompletedFinal = prevSavingStates.current.final && !isSavingToFinalArtwork;
    const justCompletedCustom = prevSavingStates.current.custom !== null && isSavingToCustomFolderId === null;
    const aSaveOperationJustCompleted = justCompletedDrafts || justCompletedFinal || justCompletedCustom;

    if (isOpen && aSaveOperationJustCompleted) {
      clearCountdown(); 
      setAutoCloseCountdown(5); 

      countdownIntervalRef.current = setInterval(() => {
        setAutoCloseCountdown(prev => {
          if (prev === null) { 
            clearCountdown();
            return null;
          }
          if (prev <= 1) { 
            clearCountdown();
            setAnimateCloseClick(true); 
            setTimeout(() => {
              if (isOpen) { 
                onClose();
              }
              setAutoCloseCountdown(null); 
              setAnimateCloseClick(false);
            }, 300); 
            return 0; 
          }
          return prev - 1;
        });
      }, 1000);
    }

    prevSavingStates.current = {
      drafts: isSavingToDrafts,
      final: isSavingToFinalArtwork,
      custom: isSavingToCustomFolderId,
    };
    return () => {
      clearCountdown();
    };
  }, [isOpen, isSavingToDrafts, isSavingToFinalArtwork, isSavingToCustomFolderId, onClose]);

  useEffect(() => {
    const anyNewOperationIsNowActive = isSavingToDrafts || isSavingToFinalArtwork || !!isSavingToCustomFolderId;
    if ((anyNewOperationIsNowActive && autoCloseCountdown !== null) || (!isOpen && autoCloseCountdown !== null)) {
      clearCountdown();
    }
  }, [isSavingToDrafts, isSavingToFinalArtwork, isSavingToCustomFolderId, isOpen, autoCloseCountdown]);

  const handleManualClose = () => {
    clearCountdown();
    onClose();
  };

  if (!isOpen || !image) {
    if (autoCloseCountdown !== null) clearCountdown(); 
    return null;
  }

  const anyOperationInProgress = isSavingToDrafts || isSavingToFinalArtwork || !!isSavingToCustomFolderId;
  const isAlreadyDraft = image.isSaved && !image.isExported && !image.savedToCustomFolderPath;
  const isAlreadyFinal = image.isExported;
  const saveToDraftsDisabled = anyOperationInProgress || isAlreadyDraft;
  const saveToFinalDisabled = anyOperationInProgress || isAlreadyFinal;
  const saveToDraftsText = isAlreadyDraft ? `Ya en ${USER_CREATED_WORK_SPANISH_NAME}` : `Guardar en ${USER_CREATED_WORK_SPANISH_NAME}`;
  const saveToFinalText = isAlreadyFinal ? `Ya en ${USER_EXPORTED_ART_SPANISH_NAME}` : `Guardar en ${USER_EXPORTED_ART_SPANISH_NAME}`;

  const handleCreateFolder = async () => {
    setAddFolderError(null);
    if (!newCustomFolderName.trim()) {
      setAddFolderError('El nombre de la carpeta no puede estar vacío.');
      return;
    }
    const success = await onAddFolder(newCustomFolderName.trim());
    if (success) {
      setNewCustomFolderName('');
      setShowAddFolderInput(false);
    } else {
      setAddFolderError(`No se pudo crear la carpeta "${newCustomFolderName.trim()}". Puede que ya exista o sea un nombre inválido.`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75 transition-opacity duration-300 ease-in-out"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-destination-modal-title"
      onClick={handleManualClose}
    >
      <div
        className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md transform transition-all duration-300 ease-in-out flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: 'calc(100vh - 4rem)' }}
      >
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h2 id="save-destination-modal-title" className="text-xl font-semibold text-indigo-300">
            Confirmar Guardado
          </h2>
          <button
            onClick={handleManualClose}
            onMouseEnter={() => onDebugHover?.(autoCloseCountdown !== null ? `SaveModal: Close Button (Countdown ${autoCloseCountdown})` : "SaveModal: Close Button")}
            onMouseLeave={() => onDebugHover?.(null)}
            disabled={anyOperationInProgress && autoCloseCountdown === null} 
            className={`p-1 rounded-full transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800
                        ${autoCloseCountdown !== null 
                            ? `w-8 h-8 flex items-center justify-center text-lg font-bold 
                               ${animateCloseClick && autoCloseCountdown === 0 ? 'transform scale-75 brightness-150 bg-red-500 text-white ring-red-300' 
                                                                               : 'text-red-400 hover:text-red-300 bg-gray-700 ring-red-400'}`
                            : 'text-gray-400 hover:text-gray-100 hover:bg-gray-700 disabled:opacity-50 ring-gray-400'
                        }`}
            aria-label={autoCloseCountdown !== null ? `Cerrando en ${autoCloseCountdown}...` : "Cerrar"}
          >
            {autoCloseCountdown !== null ? (
                <span>{autoCloseCountdown}</span>
            ) : (
                <CloseIcon className="w-6 h-6" />
            )}
          </button>
        </div>

        <p className="text-gray-300 mb-6 flex-shrink-0">
          ¿Dónde te gustaría guardar la imagen "{image.displayName || image.alt}"?
        </p>

        <div className="space-y-3 overflow-y-auto flex-grow pr-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-700">
          <button
            onClick={onSaveToDrafts}
            onMouseEnter={() => onDebugHover?.(`SaveModal: ${saveToDraftsText}`)}
            onMouseLeave={() => onDebugHover?.(null)}
            disabled={saveToDraftsDisabled}
            className="w-full px-4 py-3 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
            aria-live="polite"
          >
            {isSavingToDrafts ? (
              <><LoadingSpinner size="sm" /> <span className="ml-2">Guardando...</span></>
            ) : (
              saveToDraftsText
            )}
          </button>

          <button
            onClick={onSaveToFinalArtwork}
            onMouseEnter={() => onDebugHover?.(`SaveModal: ${saveToFinalText}`)}
            onMouseLeave={() => onDebugHover?.(null)}
            disabled={saveToFinalDisabled}
            className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
            aria-live="polite"
          >
            {isSavingToFinalArtwork ? (
              <><LoadingSpinner size="sm" /> <span className="ml-2">Grabando...</span></>
            ) : (
              saveToFinalText
            )}
          </button>

          {customFolders.length > 0 && (
            <div className="my-3 border-t border-gray-700"></div>
          )}

          {customFolders.map((folder) => {
            const isSavingThisFolder = isSavingToCustomFolderId === folder.id;
            const isAlreadyInThisFolder = !!(image.savedToCustomFolderPath && image.savedToCustomFolderPath.startsWith(GENERATED_IMAGES_FASE_PATH) && image.savedToCustomFolderPath.endsWith(`/${folder.name}/`));
            const buttonDisabled = anyOperationInProgress || isAlreadyInThisFolder;
            const buttonText = isAlreadyInThisFolder ? `Ya en ${folder.name}` : `Guardar en ${folder.name}`;
            const hoverColor = folder.color.replace('-600', '-700').replace('-500', '-600'); 

            return (
              <button
                key={folder.id}
                onClick={() => onSaveToCustomFolder(folder)}
                onMouseEnter={() => onDebugHover?.(`SaveModal: ${buttonText}`)}
                onMouseLeave={() => onDebugHover?.(null)}
                disabled={buttonDisabled}
                className={`w-full px-4 py-3 ${folder.color} hover:${hoverColor} text-white font-semibold rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-800 focus:${folder.color} focus:ring-opacity-70 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center`}
                aria-live="polite"
                title={buttonText}
              >
                {isSavingThisFolder ? (
                  <><LoadingSpinner size="sm" /> <span className="ml-2">Guardando...</span></>
                ) : (
                  <span className="truncate">{buttonText}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-700 flex-shrink-0">
          {!showAddFolderInput && (
            <button
              onClick={() => { setShowAddFolderInput(true); setAddFolderError(null); }}
              onMouseEnter={() => onDebugHover?.("SaveModal: Nueva Carpeta Button")}
              onMouseLeave={() => onDebugHover?.(null)}
              disabled={anyOperationInProgress}
              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              + Nueva Carpeta Personalizada
            </button>
          )}

          {showAddFolderInput && (
            <div className="space-y-2">
              <input
                type="text"
                value={newCustomFolderName}
                onChange={(e) => setNewCustomFolderName(e.target.value)}
                placeholder="Nombre de la nueva carpeta"
                className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 text-gray-100"
                disabled={anyOperationInProgress}
                aria-label="Nombre de la nueva carpeta"
                onMouseEnter={() => onDebugHover?.("SaveModal Input: New Folder Name")}
                onMouseLeave={() => onDebugHover?.(null)}
              />
              {addFolderError && <p className="text-xs text-red-400">{addFolderError}</p>}
              <div className="flex space-x-2">
                <button
                  onClick={handleCreateFolder}
                  onMouseEnter={() => onDebugHover?.("SaveModal Button: Create Folder")}
                  onMouseLeave={() => onDebugHover?.(null)}
                  disabled={anyOperationInProgress || !newCustomFolderName.trim()}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md transition duration-150 ease-in-out disabled:opacity-60"
                >
                  Crear Carpeta
                </button>
                <button
                  onClick={() => { setShowAddFolderInput(false); setNewCustomFolderName(''); setAddFolderError(null); }}
                  onMouseEnter={() => onDebugHover?.("SaveModal Button: Cancel Create Folder")}
                  onMouseLeave={() => onDebugHover?.(null)}
                  disabled={anyOperationInProgress}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white font-semibold rounded-md transition duration-150 ease-in-out disabled:opacity-60"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
        
        {anyOperationInProgress && autoCloseCountdown === null && (
            <p className="text-xs text-yellow-400 mt-3 text-center flex-shrink-0">
                Por favor, espera mientras se completa la operación de guardado.
            </p>
        )}
         {autoCloseCountdown !== null && (
            <p className="text-xs text-sky-300 mt-3 text-center flex-shrink-0">
                {autoCloseCountdown === 0 ? 'Cerrando...' : `Cerrando automáticamente en ${autoCloseCountdown}s...`}
            </p>
        )}
      </div>
    </div>
  );
};

export default SaveDestinationModal;
